<template>
  <div class="estatisticas-container">
    <div class="d-flex align-items-center mb-4">
      <button class="btn-back" @click="$router.push('/')">
        <i class="fas fa-arrow-left"></i>
      </button>
      <h1 class="page-title ml-3">Estatísticas</h1>
    </div>
    <main class="main">
      <div class="totals-container">
        <b-spinner v-if="loadingTotals" />
        <div v-if="!loadingTotals && totals" class="stats-grid">
          <div class="stat-card-modern">
            <div class="stat-icon-bg">
              <i class="fas fa-building"></i>
            </div>
            <h2 class="stat-card-title">Empresas Ativas</h2>
            <p class="stat-card-number">{{ totals.activeCompanies }}</p>
            <p class="stat-card-info">Total Cadastrado: {{ totals.totalCompanies }}</p>
          </div>
          <div class="stat-card-modern">
            <div class="stat-icon-bg">
              <i class="fas fa-users"></i>
            </div>
            <h2 class="stat-card-title">Funcionários Ativos</h2>
            <p class="stat-card-number">{{ totals.activeEmployees }}</p>
            <p class="stat-card-info">Total Cadastrado: {{ totals.totalEmployees }}</p>
          </div>
          <div class="stat-card-modern">
            <div class="stat-icon-bg">
              <i class="fas fa-tools"></i>
            </div>
            <h2 class="stat-card-title">Equipamentos em Uso</h2>
            <p class="stat-card-number">{{ totals.activeEquipments }}</p>
            <p class="stat-card-info">Total Cadastrado: {{ totals.totalEquipments }}</p>
          </div>
        </div>
      </div>
      <section class="charts-section">
        <h2 class="section-title">Incidência de Defeitos</h2>
        <div class="chart-filters">
          <div class="filter-group filter-group--period">
            <label class="filter-label">Período</label>
            <date-range-picker
              :start-date.sync="dateRangeStart"
              :end-date.sync="dateRangeEnd"
              placeholder="Selecione o período"
            />
          </div>

          <div v-if="isSuperAdmin" class="filter-group">
            <label class="filter-label">Empresa</label>
            <b-form-select
              v-model="selectedCompanyId"
              :options="companyOptions"
              class="company-select"
              @change="onCompanyChange"
            />
          </div>

          <localidade-filter
            :state.sync="selectedLocationState"
            :city.sync="selectedLocationCity"
            layout="group"
            select-class="company-select"
            @change="onLocationFilterChange"
          />

          <class-multi-select-filter
            v-model="selectedClasses"
            :options="classOptions"
            @change="applyChartFilters"
          />
        </div>
        <div class="chart-box">
          <statistics-chart
            :loading="loadingStats"
            :no-data="noDataForYear"
            :chart-type.sync="selectedChartType"
            :raw-data="chartRawData"
            :chart-classes="chartClasses"
            :column-list="chartColumnList"
            :selected-classes="selectedClasses"
            :group-by="apiGroupBy"
          />
        </div>

        <div class="table-box">
          <b-spinner v-if="loadingStats" />

          <p v-if="!loadingStats && noDataForYear" class="empty-state-msg">
            Nenhum dado de análise encontrado para o período selecionado.
          </p>

          <template v-if="!loadingStats && fields.length > 0 && tableItems.length > 0">

            <div class="table-header-bar">
              <div>
                <h3 class="table-title">Incidência de Defeitos por Classe</h3>
                <p class="table-subtitle">{{ tableSubtitle }}</p>
              </div>
              <div class="table-header-actions">
                <div class="table-search">
                  <i class="fas fa-search"></i>
                  <input v-model="tableSearch" type="text" placeholder="Buscar classe..." />
                </div>
              </div>
            </div>

            <div class="table-responsive-wrap">
              <table class="modern-table">
                <thead>
                  <tr>
                    <th v-for="field in fields" :key="field.key">
                      {{ getColumnLabel(field.key) }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in sortedFilteredTableItems" :key="item.classe" :class="rowClass(item)">
                    <td v-for="field in fields" :key="field.key">
                      <span
                        v-if="field.key !== 'classe'"
                        :class="['value-badge', badgeClass(item[field.key], item.classe)]"
                        :title="standardTooltip(item.classe)"
                      >
                        {{ item[field.key] }}
                      </span>
                      <span v-else>{{ item.classe }}</span>
                    </td>
                  </tr>
                  <tr class="total-row">
                    <td>Total</td>
                    <td v-for="month in monthColumns" :key="month">
                      <span class="value-badge badge-neutral">
                        {{ totalsByMonth[month] }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="intensity-legend">
              <span>Padrão básico:</span>
              <span class="legend-item"><i class="legend-dot dot-low"></i> Dentro do padrão</span>
              <span class="legend-item"><i class="legend-dot dot-high"></i> Fora do padrão</span>
              <span class="legend-item"><i class="legend-dot dot-neutral"></i> Sem limite definido</span>
            </div>
          </template>
        </div>
      </section>
    </main>
  </div>
</template>

<style scoped>
.estatisticas-container {
  animation: fadeIn 0.3s ease-in;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.btn-back {
  background: linear-gradient(135deg, #FF8C42 0%, #FF7A29 100%);
  border: none;
  color: white;
  width: 45px;
  height: 45px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(255, 140, 66, 0.3);
}

.btn-back:hover {
  background: linear-gradient(135deg, #FF7A29 0%, #FF6810 100%);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(255, 140, 66, 0.4);
}

.btn-back i {
  font-size: 1.2rem;
}

.page-title {
  font-size: 2rem;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 0;
}

.totals-container {
  margin-bottom: 50px;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 25px;
}

.stat-card-modern {
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border-radius: 20px;
  padding: 30px;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.stat-card-modern:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 30px rgba(255, 140, 66, 0.2);
  border-color: #FF8C42;
}

.stat-icon-bg {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #FF8C42 0%, #FF7A29 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  transition: all 0.3s ease;
}

.stat-card-modern:hover .stat-icon-bg {
  transform: scale(1.1) rotate(5deg);
}

.stat-icon-bg i {
  font-size: 2.2rem;
  color: white;
}

.stat-card-title {
  font-size: 1.3rem;
  font-weight: 600;
  color: #2c3e50;
  margin: 0 0 15px 0;
}

.stat-card-number {
  font-size: 2.5rem;
  font-weight: 700;
  color: #FF8C42;
  margin: 0 0 10px 0;
}

.stat-card-info {
  font-size: 1rem;
  color: #6c757d;
  margin: 0;
}

.charts-section {
  margin-top: 40px;
}

.section-title {
  font-size: 1.7rem;
  font-weight: 700;
  color: #2c3e50;
  margin: 0 0 25px 0;
}

.chart-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 2rem;
  margin-bottom: 25px;
  align-items: flex-start;
}

.filter-group--period {
  min-width: 280px;
  flex: 1;
  max-width: 360px;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.filter-label {
  font-weight: 600;
  color: #2c3e50;
  font-size: 1rem;
  margin: 0;
}

.filter-hint {
  font-weight: 400;
  color: #6c757d;
  font-size: 0.85rem;
}

.filter-group--chart-type {
  min-width: 0;
}

.chart-type-group {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.chart-type-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 14px;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  background: #ffffff;
  color: #6c757d;
  font-size: 0.95rem;
  font-weight: 500;
  line-height: 1.4;
  cursor: pointer;
  transition: border-color 0.2s ease, color 0.2s ease, background 0.2s ease;
  white-space: nowrap;
}

.chart-type-btn i {
  font-size: 0.9rem;
}

.chart-type-btn:hover {
  border-color: #FF8C42;
  color: #FF8C42;
}

.chart-type-btn.active {
  background: linear-gradient(135deg, #FF8C42 0%, #FF7A29 100%);
  border-color: #FF8C42;
  color: white;
}

.chart-type-btn.active i {
  color: white;
}

.filter-select-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
}

.filter-select-icon {
  flex-shrink: 0;
  color: #FF8C42;
  font-size: 1rem;
  width: 18px;
  text-align: center;
}

.filter-select {
  flex: 1;
  width: 100%;
  min-width: 140px;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  padding: 12px 40px 12px 16px;
  border: 2px solid #FF8C42;
  border-radius: 12px;
  background-color: #ffffff;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%23FF8C42' d='M1.41 0L6 4.58 10.59 0 12 1.41l-6 6-6-6z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 14px center;
  color: #2c3e50;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  line-height: 1.4;
}

.filter-select:hover {
  border-color: #FF7A29;
  box-shadow: 0 0 0 3px rgba(255, 140, 66, 0.15);
}

.filter-select:focus {
  outline: none;
  border-color: #FF7A29;
  box-shadow: 0 0 0 3px rgba(255, 140, 66, 0.2);
}

.class-filter-group {
  min-width: 280px;
  flex: 1;
  max-width: 360px;
}

.class-dropdown {
  position: relative;
}

.class-dropdown-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-width: 0;
  padding: 12px 16px;
  border: 2px solid #FF8C42;
  border-radius: 12px;
  background: white;
  color: #2c3e50;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.class-dropdown-trigger:hover {
  border-color: #FF7A29;
  box-shadow: 0 0 0 3px rgba(255, 140, 66, 0.15);
}

.class-dropdown-trigger i {
  color: #FF8C42;
  transition: transform 0.3s ease;
}

.selected-classes-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.class-dropdown-menu {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  background: white;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  max-height: 300px;
  overflow-y: auto;
  animation: dropdownFade 0.2s ease;
}

@keyframes dropdownFade {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.class-option {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  border-bottom: 1px solid #f0f0f0;
}

.class-option:last-child {
  border-bottom: none;
}

.class-option:hover:not(.disabled) {
  background: #FFF5EF;
}

.class-option.selected {
  background: linear-gradient(135deg, #FFF5EF 0%, #FFE8D9 100%);
}

.class-option.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.class-checkbox {
  width: 22px;
  height: 22px;
  border: 2px solid #d0d0d0;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.class-option.selected .class-checkbox {
  background: linear-gradient(135deg, #FF8C42 0%, #FF7A29 100%);
  border-color: #FF8C42;
}

.class-checkbox i {
  color: white;
  font-size: 0.75rem;
}

.class-name {
  font-size: 0.95rem;
  color: #2c3e50;
}

.selected-classes-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}

.class-tag {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: linear-gradient(135deg, #FF8C42 0%, #FF7A29 100%);
  color: white;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 500;
  animation: tagPop 0.2s ease;
}

@keyframes tagPop {
  from {
    transform: scale(0.8);
    opacity: 0;
  }

  to {
    transform: scale(1);
    opacity: 1;
  }
}

.class-tag i {
  cursor: pointer;
  opacity: 0.8;
  transition: opacity 0.2s ease;
  font-size: 0.75rem;
}

.class-tag i:hover {
  opacity: 1;
}

.filter-select {
  border: 2px solid #FF8C42;
  border-radius: 8px;
  padding: 8px 12px;
  font-weight: 500;
  transition: all 0.3s ease;
}

.filter-select:focus {
  border-color: #FF7A29;
  box-shadow: 0 0 0 0.2rem rgba(255, 140, 66, 0.25);
}

.chart-box,
.table-box {
  padding: 30px;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border-radius: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  margin-bottom: 25px;
  transition: all 0.3s ease;
}

.chart-box:hover,
.table-box:hover {
  box-shadow: 0 6px 30px rgba(0, 0, 0, 0.12);
}

.form-group {
  margin: 0;
}

.table-header-bar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 20px;
}

.table-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #2c3e50;
  margin: 0 0 4px 0;
}

.table-subtitle {
  font-size: 0.9rem;
  color: #6c757d;
  margin: 0;
}

.table-header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

/* Wrapper de scroll horizontal (substitui a prop "responsive" do b-table) */
.table-responsive-wrap {
  width: fit-content;
  max-width: 100%;
  margin: 0 auto;
  overflow-x: auto;
}

.table-responsive-wrap .modern-table {
  width: auto;
  table-layout: auto;
}

.table-responsive-wrap .modern-table th,
.table-responsive-wrap .modern-table td {
  width: auto;
  white-space: nowrap;
}

.empty-state-msg {
  text-align: center;
  color: #6c757d;
  font-size: 1rem;
  padding: 30px 10px;
  margin: 0;
}

.table-search {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border: 2px solid #e0e0e0;
  border-radius: 10px;
  background: white;
  min-width: 220px;
}

.table-search i {
  color: #9aa3ab;
}

.table-search input {
  border: none;
  outline: none;
  font-size: 0.9rem;
  width: 100%;
  color: #2c3e50;
}

/* ---- Modern Table ---- */
::v-deep(.modern-table) {
  border-radius: 12px;
  overflow: hidden;
  font-variant-numeric: tabular-nums;
}

::v-deep(.modern-table thead) {
  background: linear-gradient(135deg, #FF8C42 0%, #FF7A29 100%);
  color: white;
}

::v-deep(.modern-table thead th) {
  font-weight: 700;
  text-transform: uppercase;
  font-size: 0.85rem;
  letter-spacing: 0.5px;
  padding: 16px;
  border: none;
  white-space: nowrap;
}

::v-deep(.modern-table thead th:not(:first-child)) {
  text-align: right;
}

::v-deep(.modern-table tbody tr) {
  transition: all 0.2s ease;
}

::v-deep(.modern-table tbody tr:nth-of-type(even)) {
  background-color: #fcfcfc;
}

::v-deep(.modern-table tbody tr:hover) {
  background-color: #FFF5EF;
  transform: scale(1.005);
}

::v-deep(.modern-table tbody td) {
  padding: 16px;
  vertical-align: middle;
  font-size: 0.95rem;
}

::v-deep(.modern-table tbody td:not(:first-child)) {
  text-align: right;
}

::v-deep(.row-active) {
  background-color: #FFB380 !important;
  font-weight: 600;
}

.total-row td {
  padding: 16px;
  font-weight: 800;
  color: #2c3e50;
  background: #FFF5EF;
  border-top: 2px solid #FF8C42;
}

.total-row td:first-child {
  text-align: left;
}

.total-row td:not(:first-child) {
  text-align: right;
}

/* ---- Colored badges (padrão básico) ---- */
.value-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 20px;
  font-weight: 700;
  font-size: 0.85rem;
  font-family: 'Courier New', monospace;
}

.badge-neutral {
  background: #f1f3f5;
  color: #868e96;
}

.badge-within {
  background: #d3f9d8;
  color: #2b8a3e;
}

.badge-outside {
  background: #ffe3e3;
  color: #c92a2a;
}

/* ---- Legend ---- */
.intensity-legend {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 16px;
  font-size: 0.85rem;
  color: #6c757d;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
}

.dot-neutral {
  background: #adb5bd;
}

.dot-low {
  background: #40c057;
}

.dot-high {
  background: #e03131;
}

/* Tablet */
@media (max-width: 991px) {
  .page-title {
    font-size: 1.75rem;
  }

  .stat-card-modern {
    padding: 20px;
  }

  .stat-icon-bg {
    width: 60px;
    height: 60px;
  }

  .stat-icon-bg i {
    font-size: 1.8rem;
  }

  .stat-card-number {
    font-size: 2rem;
  }

  .chart-box,
  .table-box {
    padding: 20px;
  }

  .class-dropdown-trigger {
    min-width: 220px;
  }
}

/* Mobile */
@media (max-width: 768px) {
  .page-title {
    font-size: 1.5rem;
  }

  .stats-grid {
    grid-template-columns: 1fr;
  }

  .chart-filters {
    flex-direction: column;
    gap: 20px;
  }

  .filter-group--period,
  .class-filter-group {
    max-width: none;
    width: 100%;
  }

  .filter-group {
    width: 100%;
  }

  .chart-type-group {
    width: 100%;
  }

  .chart-type-btn {
    flex: 1;
    justify-content: center;
    padding: 10px 12px;
    font-size: 0.9rem;
  }

  .class-filter-group {
    width: 100%;
  }

  .class-dropdown-trigger {
    width: 100%;
  }

  .filter-select {
    width: 100%;
  }

  .stat-card-modern {
    padding: 16px;
    border-radius: 16px;
  }

  .stat-icon-bg {
    width: 50px;
    height: 50px;
    margin-bottom: 15px;
  }

  .stat-icon-bg i {
    font-size: 1.5rem;
  }

  .stat-card-title {
    font-size: 1.1rem;
  }

  .stat-card-number {
    font-size: 1.8rem;
  }

  .stat-card-info {
    font-size: 0.9rem;
  }

  .section-title {
    font-size: 1.4rem;
  }

  .chart-box,
  .table-box {
    padding: 16px;
    border-radius: 16px;
    overflow-x: auto;
  }

  ::v-deep(.modern-table thead th) {
    padding: 10px 8px;
    font-size: 0.75rem;
  }

  ::v-deep(.modern-table tbody td) {
    padding: 10px 8px;
    font-size: 0.85rem;
  }

  .table-header-bar {
    flex-direction: column;
  }

  .table-search {
    min-width: unset;
    flex: 1;
  }
}

/* Small mobile */
@media (max-width: 576px) {
  .btn-back {
    width: 40px;
    height: 40px;
    min-width: 40px;
    border-radius: 10px;
  }

  .btn-back i {
    font-size: 1rem;
  }

  .page-title {
    font-size: 1.25rem;
  }

  .totals-container {
    margin-bottom: 30px;
  }

  .stats-grid {
    gap: 15px;
  }

  .stat-card-modern {
    padding: 14px;
    border-radius: 12px;
  }

  .stat-icon-bg {
    width: 45px;
    height: 45px;
    margin-bottom: 12px;
  }

  .stat-icon-bg i {
    font-size: 1.3rem;
  }

  .stat-card-title {
    font-size: 1rem;
    margin-bottom: 10px;
  }

  .stat-card-number {
    font-size: 1.5rem;
  }

  .stat-card-info {
    font-size: 0.8rem;
  }

  .section-title {
    font-size: 1.2rem;
    margin-bottom: 16px;
  }

  .chart-box,
  .table-box {
    padding: 12px;
    border-radius: 12px;
    margin-bottom: 16px;
  }

  .charts-section {
    margin-top: 20px;
  }

  .chart-type-btn {
    padding: 10px 12px;
    font-size: 0.9rem;
  }

  .chart-type-btn i {
    font-size: 0.85rem;
  }

  .class-dropdown-trigger {
    padding: 10px 14px;
    font-size: 0.95rem;
  }

  .class-option {
    padding: 10px 14px;
  }

  .class-name {
    font-size: 0.9rem;
  }

  .class-tag {
    padding: 5px 10px;
    font-size: 0.8rem;
  }

  ::v-deep(.modern-table thead th) {
    padding: 8px 6px;
    font-size: 0.7rem;
  }

  ::v-deep(.modern-table tbody td) {
    padding: 8px 6px;
    font-size: 0.8rem;
    white-space: nowrap;
  }
}
</style>

<script>
/* eslint-disable space-before-function-paren */
import moment from 'moment'
import { api } from '@/services'
import { DB_ROUTES } from '../../../constants'
import { getCompanyIdFromToken } from '../../../utils'
import {
  ALL_CLASSES,
  classesInPortuguese,
  resolveClassStatKey,
  getPeriodSortKey,
  formatPeriodLabel,
  getApiGroupBy,
  buildClassFilterOptions,
  getBasicStandardLimit,
  getStandardCompliance
} from '@/utils/defectStatistics'

export default {
  name: 'estatisticas',
  data() {
    return {
      totals: null,
      loadingTotals: true,
      loadingStats: true,
      noDataForYear: false,
      dateRangeStart: (() => {
        const today = new Date()
        return `${today.getFullYear()}-01-01`
      })(),
      dateRangeEnd: (() => {
        const today = new Date()
        const month = String(today.getMonth() + 1).padStart(2, '0')
        const day = String(today.getDate()).padStart(2, '0')
        return `${today.getFullYear()}-${month}-${day}`
      })(),
      selectedCompanyId: null,
      companyOptions: [{ value: null, text: 'Todas as empresas' }],
      selectedLocationState: null,
      selectedLocationCity: null,
      selectedClasses: [ALL_CLASSES],
      selectedChartType: 'bar',
      classOptions: [ALL_CLASSES],
      chartRawData: null,
      chartClasses: [],
      chartColumnList: [],
      tableItems: [],
      fields: [],
      tableSearch: '',
      ALL_CLASSES
    }
  },
  computed: {
    isSuperAdmin() {
      return this.$store.getters.isSuperAdmin
    },
    apiGroupBy() {
      return getApiGroupBy(this.dateRangeStart, this.dateRangeEnd)
    },
    filteredColumnList() {
      return this.chartColumnList
    },
    tableSubtitle() {
      const labels = {
        day: 'Média percentual agrupada por dia.',
        week: 'Média percentual agrupada por semana.',
        month: 'Média percentual agrupada por mês.'
      }
      return labels[this.apiGroupBy] || labels.month
    },
    // NEW: columns that hold data values (everything except "classe")
    dataFields() {
      return this.fields.filter(f => f.key !== 'classe')
    },
    monthColumns() {
      return this.dataFields.map(f => f.key)
    },
    // NEW: rows filtered by the search box
    filteredTableItems() {
      if (!this.tableSearch) return this.tableItems
      const term = this.tableSearch.toLowerCase()
      return this.tableItems.filter(item =>
        item.classe.toLowerCase().includes(term)
      )
    },
    // NEW: rows ordered alphabetically by classe (substitui o sort-by="classe" do b-table)
    sortedFilteredTableItems() {
      return [...this.filteredTableItems].sort((a, b) => a.classe.localeCompare(b.classe))
    },
    // NEW: sum of all classes per month, to build the "Total" row
    totalsByMonth() {
      const totals = {}
      this.monthColumns.forEach(month => {
        const sum = this.tableItems.reduce((acc, item) => {
          const raw = item[month]
          const num = raw ? parseFloat(String(raw).replace('%', '').replace(',', '.')) : 0
          return acc + (isNaN(num) ? 0 : num)
        }, 0)
        totals[month] = `${sum.toFixed(2)}%`
      })
      return totals
    }
  },
  mounted() {
    this.fetchTotals()
    if (this.isSuperAdmin) {
      this.fetchCompanies()
    }
    this.fetchAnalysesStats()
  },
  watch: {
    dateRangeStart: 'onDateRangeChange',
    dateRangeEnd: 'onDateRangeChange'
  },
  methods: {
    onDateRangeChange() {
      if (this.dateRangeStart && this.dateRangeEnd) {
        this.fetchAnalysesStats()
      }
    },
    onCompanyChange() {
      this.fetchAnalysesStats()
    },
    onLocationFilterChange() {
      this.fetchAnalysesStats()
    },
    async fetchCompanies() {
      if (this.companyOptions.length > 1) return

      try {
        const response = await api.get(DB_ROUTES.GET_ALL_COMPANIES)
        const companies = (response.data || [])
          .map(company => ({
            value: company.id,
            text: company.name
          }))
          .sort((a, b) => a.text.localeCompare(b.text, 'pt-BR'))

        this.companyOptions = [
          { value: null, text: 'Todas as empresas' },
          ...companies
        ]
      } catch (error) {
        console.error('Error fetching companies:', error)
      }
    },
    resolveStatsCompanyId() {
      if (this.isSuperAdmin) {
        const selectedId = this.selectedCompanyId
        return selectedId ? Number(selectedId) : 0
      }

      return getCompanyIdFromToken()
    },
    getFetchYearRanges() {
      const start = moment(this.dateRangeStart)
      const end = moment(this.dateRangeEnd)
      const ranges = []

      for (let year = start.year(); year <= end.year(); year++) {
        ranges.push({
          year,
          startMonth: year === start.year() ? start.month() + 1 : 1,
          endMonth: year === end.year() ? end.month() + 1 : 12
        })
      }

      return ranges
    },
    getColumnLabel(key) {
      if (key === 'classe') return 'Classe'
      return formatPeriodLabel(key, this.apiGroupBy)
    },
    async fetchTotals() {
      try {
        this.loadingTotals = true
        const totalCompanies = await this.countCompanies()
        const totalEmployees = await this.countEmployees()
        const totalEquipments = await this.countEquipments()

        this.totals = {
          totalCompanies,
          activeCompanies: totalCompanies,
          totalEmployees,
          activeEmployees: totalEmployees,
          totalEquipments,
          activeEquipments: totalEquipments
        }
      } catch (error) {
        console.log('Error while fetching totals data')
        console.log(error)
      } finally {
        this.loadingTotals = false
      }
    },
    async countCompanies() {
      try {
        const companies = await api.get(DB_ROUTES.GET_ALL_COMPANIES)
        return companies.data.length
      } catch (error) {
        console.log(`Error fetching companies: ${error}`)
      }
    },
    async countEmployees() {
      try {
        const employees = await api.get(DB_ROUTES.GET_ALL_EMPLOYEES)
        return employees.data.length
      } catch (error) {
        console.log(`Error fetching employees: ${error}`)
      }
    },
    async countEquipments() {
      try {
        const equipments = await api.get(DB_ROUTES.GET_ALL_EQUIPMENTS)
        return equipments.data.length
      } catch (error) {
        console.log(`Error fetching equipments: ${error}`)
      }
    },
    async fetchAnalysesStats() {
      if (!this.dateRangeStart || !this.dateRangeEnd) return

      const start = moment(this.dateRangeStart, 'YYYY-MM-DD')
      const end = moment(this.dateRangeEnd, 'YYYY-MM-DD')
      if (end.isBefore(start)) return

      this.loadingStats = true
      this.noDataForYear = false

      try {
        let companyId = this.resolveStatsCompanyId()

        if (!this.isSuperAdmin && !companyId) {
          try {
            const userId = this.$store.state.user.id
            const response = await api.get(DB_ROUTES.GET_EMPLOYEE_BY_ID(userId))
            companyId = response.data?.companyId || null
          } catch (error) {
            console.error('Error resolving company id:', error)
          }
        }

        if (!this.isSuperAdmin && !companyId) {
          console.error('CompanyId not found in user token')
          this.noDataForYear = true
          return
        }

        const groupBy = this.apiGroupBy
        const yearRanges = this.getFetchYearRanges()
        const responses = await Promise.all(
          yearRanges.map(({ year, startMonth, endMonth }) =>
            api.get(
              DB_ROUTES.GET_ANALYSES_STATISTICS_BY_COMPANY_DATE(
                year,
                startMonth,
                endMonth,
                Number(companyId),
                groupBy,
                this.dateRangeStart,
                this.dateRangeEnd,
                this.selectedLocationState,
                this.selectedLocationCity
              )
            )
          )
        )

        const data = responses.reduce((merged, response) => ({
          ...merged,
          ...(response.data || {})
        }), {})
        const columnList = Object.keys(data)
          .sort((a, b) => getPeriodSortKey(a) - getPeriodSortKey(b))

        if (!columnList.length) {
          this.tableItems = []
          this.fields = []
          this.chartRawData = null
          this.chartClasses = []
          this.chartColumnList = []
          this.noDataForYear = true
          return
        }

        const classes = Object.keys(data[columnList[0]])
          .map(name => resolveClassStatKey(name))
          .filter(Boolean)

        this.classOptions = buildClassFilterOptions(classes)

        this.chartRawData = data
        this.chartClasses = classes
        this.chartColumnList = columnList
        this.applyChartFilters()
      } catch (error) {
        console.log(error)
        this.tableItems = []
        this.fields = []
        this.chartRawData = null
        this.chartClasses = []
        this.chartColumnList = []
        this.noDataForYear = true
      } finally {
        this.loadingStats = false
      }
    },
    populateTable(data, classes, columnList) {
      const tableData = []
      classes.forEach(className => {
        const nameInPortuguese = classesInPortuguese[className]
        if (!nameInPortuguese) return

        const resultsData = { classe: nameInPortuguese }
        columnList.forEach(period => {
          resultsData[period] = `${data[period][className].mean}%`
        })
        tableData.push(resultsData)
      })

      this.tableItems = tableData

      this.fields = Object.keys(tableData[0]).map(column => ({
        key: column,
        sortable: column === 'classe',
        thClass: 'table-header'
      }))
    },
    applyChartFilters() {
      if (!this.chartRawData || !this.chartClasses.length || !this.chartColumnList.length) {
        this.tableItems = []
        this.fields = []
        return
      }

      const columnList = this.filteredColumnList
      if (!columnList.length) {
        this.tableItems = []
        this.fields = []
        return
      }

      this.populateTable(this.chartRawData, this.chartClasses, columnList)
    },
    rowClass (item) {
      if (item && this.selectedClasses.includes(item.classe) && !this.selectedClasses.includes(ALL_CLASSES)) {
        return 'row-active'
      }
    },
    badgeClass (value, classLabel) {
      const status = getStandardCompliance(value, classLabel)
      if (status === 'within') return 'badge-within'
      if (status === 'outside') return 'badge-outside'
      return 'badge-neutral'
    },
    standardTooltip (classLabel) {
      const limit = getBasicStandardLimit(classLabel)
      if (limit == null) return 'Sem limite de padrão básico para esta classe'
      return `Padrão básico: até ${limit.toFixed(1).replace('.', ',')}%`
    }
  }
}
</script>
